/**
 * ado-motion: one-shot reveals, in-view animation gating and small motion helpers.
 * Imported as "@theme/ado-motion" (see snippets/scripts.liquid) and loaded once from layout/theme.liquid.
 *
 * Markup API
 *   [data-ado-reveal]            fade up once when it enters view (variants: rise, fade, left, right, zoom, draw)
 *   [data-ado-stagger]           on a parent: children get --ado-i = 0,1,2… for staggered delays
 *   [data-ado-inview]            gets .is-inview while visible; CSS pauses its animations otherwise
 *   Keyboard focus inside a reveal that has not played yet shows it (and any hidden ancestor reveal) at once.
 *   Content added to the page later (AJAX re-renders, sorting) is picked up by a MutationObserver on its own.
 *
 * JS API
 *   ioRoot()                     IntersectionObserver root for the current scroller (.page-wrapper at >=990px, else null)
 *   scan(scope)                  observe new [data-ado-reveal]/[data-ado-inview] elements inside scope
 *   onInView(el, cb, options)    cb(isIntersecting, entry) with the right root; returns a disconnect function
 *   reducedMotion()              true when the visitor prefers reduced motion
 *   burst(target, options)       6-particle "oat flake" burst from an element (skipped under reduced motion)
 *   flyToCart(source)            a copy of a product image arcs into the nav cart icon, which then "catches" it;
 *                                resolves when it lands (at once under reduced motion or when nothing is on screen)
 *
 * Keyboard scrolling at >=990px: Horizon scrolls .page-wrapper there (base.css), not the document. With focus on
 * <body> or inside a fixed layer (the nav, the toast), Chrome sends PageDown / Space / arrows / Home / End to the
 * root, which cannot scroll, so the keys did nothing. A keydown bridge forwards them to .page-wrapper. Focus inside
 * the page content scrolls natively and is left alone.
 */
import { getIntersectionRoot, getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

window.__adoMotion = true;

const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {IntersectionObserver | undefined} */
let revealIO;
/** @type {IntersectionObserver | undefined} */
let inviewIO;

export const ioRoot = getIntersectionRoot;

export function reducedMotion() {
  return reduceQuery.matches;
}

function build() {
  revealIO?.disconnect();
  inviewIO?.disconnect();
  const root = ioRoot();
  revealIO = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        revealIO?.unobserve(entry.target);
      }
    },
    { root, rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );
  inviewIO = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) entry.target.classList.toggle('is-inview', entry.isIntersecting);
    },
    { root, rootMargin: '80px 0px' }
  );
  scan(document);
}

/**
 * Observes reveal and in-view elements inside a scope (a document, a section or any element).
 * @param {ParentNode} [scope]
 */
export function scan(scope = document) {
  if (!revealIO || !inviewIO) return;
  for (const group of scope.querySelectorAll('[data-ado-stagger]')) {
    [...group.children].forEach((child, i) => {
      if (child instanceof HTMLElement || child instanceof SVGElement) child.style.setProperty('--ado-i', String(i));
    });
  }
  for (const el of scope.querySelectorAll('[data-ado-reveal]:not(.is-in)')) {
    if (reduceQuery.matches) el.classList.add('is-in');
    else revealIO.observe(el);
  }
  for (const el of scope.querySelectorAll('[data-ado-inview]')) inviewIO.observe(el);
}

/**
 * Runs a callback whenever an element enters or leaves view, using the correct scroll root.
 * @param {Element} el
 * @param {(isIntersecting: boolean, entry: IntersectionObserverEntry) => void} callback
 * @param {IntersectionObserverInit} [options]
 * @returns {() => void} disconnect
 */
export function onInView(el, callback, options = {}) {
  /** @type {IntersectionObserver | undefined} */
  let io;
  const start = () => {
    io?.disconnect();
    io = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) callback(entry.isIntersecting, entry);
    }, { root: ioRoot(), ...options });
    io.observe(el);
  };
  start();
  scrollContainerMediaQuery.addEventListener('change', start);
  return () => {
    io?.disconnect();
    scrollContainerMediaQuery.removeEventListener('change', start);
  };
}

const BURST_COLORS = ['#ffd45d', '#ffb56b', '#6eb0eb', '#ff9b98', '#fff1d6', '#d9ccff'];

/**
 * Small celebratory burst of oat flakes from the centre of an element (for example after Add to cart).
 * @param {Element} target
 * @param {{ count?: number, spread?: number }} [options]
 */
export function burst(target, { count = 6, spread = 56 } = {}) {
  if (reduceQuery.matches || !target?.getBoundingClientRect) return;
  const rect = target.getBoundingClientRect();
  const layer = document.createElement('span');
  layer.className = 'ado-burst';
  layer.setAttribute('aria-hidden', 'true');
  layer.style.left = `${rect.left + rect.width / 2}px`;
  layer.style.top = `${rect.top + rect.height / 2}px`;
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.6 - 0.3;
    const distance = spread * (0.7 + Math.random() * 0.5);
    const p = document.createElement('span');
    p.className = 'ado-burst__p';
    p.style.setProperty('--x', `${Math.round(Math.cos(angle) * distance)}px`);
    p.style.setProperty('--y', `${Math.round(Math.sin(angle) * distance - 18)}px`);
    p.style.setProperty('--r', `${Math.round(Math.random() * 240 - 120)}deg`);
    p.style.setProperty('--c', BURST_COLORS[i % BURST_COLORS.length] ?? '#ffd45d');
    layer.append(p);
  }
  document.body.append(layer);
  window.setTimeout(() => layer.remove(), 760);
}

/**
 * Flies a copy of a product image into the nav cart icon (a lifted arc that shrinks and tilts), then the bag
 * "catches" it. Callers update the cart badge when the promise resolves, so the count bumps on landing.
 * Resolves at once under reduced motion, or when the image or the cart icon is not on screen.
 * @param {Element | null | undefined} source - an <img>, or an element that contains one
 * @returns {Promise<void>}
 */
export function flyToCart(source) {
  const cart = document.querySelector('[data-ado-nav-cart]');
  const img = source instanceof HTMLImageElement ? source : source?.querySelector('img');
  if (reduceQuery.matches || !cart || !(img instanceof HTMLImageElement) || !img.complete || !img.naturalWidth) {
    return Promise.resolve();
  }
  const to = cart.getBoundingClientRect();
  const from = img.getBoundingClientRect();
  const vh = window.innerHeight;
  if (!to.width || !from.width || from.bottom < 0 || from.top > vh || typeof img.animate !== 'function') {
    return Promise.resolve();
  }
  const style = getComputedStyle(img);
  const w = img.offsetWidth || from.width;
  const h = img.offsetHeight || from.height;
  const cx = from.left + from.width / 2;
  const cy = from.top + from.height / 2;
  const dx = to.left + to.width / 2 - cx;
  const dy = to.top + to.height / 2 - cy;
  const end = Math.min(1, 26 / Math.max(w, h));
  const mid = Math.min(1, Math.max(end * 2.4, 0.5));
  const lift = Math.min(140, 48 + Math.abs(dy) * 0.18);

  const clone = document.createElement('img');
  clone.className = 'ado-fly';
  clone.alt = '';
  clone.setAttribute('aria-hidden', 'true');
  clone.decoding = 'sync';
  clone.src = img.currentSrc || img.src;
  clone.style.left = `${cx - w / 2}px`;
  clone.style.top = `${cy - h / 2}px`;
  clone.style.width = `${w}px`;
  clone.style.height = `${h}px`;
  clone.style.objectFit = style.objectFit;
  clone.style.rotate = style.rotate === 'none' ? '0deg' : style.rotate;
  document.body.append(clone);

  const flight = clone.animate(
    [
      { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1 },
      { transform: `translate(0, -10px) scale(1.06) rotate(-3deg)`, opacity: 1, offset: 0.12 },
      { transform: `translate(${dx * 0.42}px, ${dy * 0.42 - lift}px) scale(${mid}) rotate(-10deg)`, opacity: 1, offset: 0.5 },
      { transform: `translate(${dx}px, ${dy}px) scale(${end}) rotate(-22deg)`, opacity: 0.6 },
    ],
    { duration: 720, easing: 'cubic-bezier(.45,.05,.3,1)', fill: 'forwards' }
  );

  return flight.finished
    .catch(() => {})
    .then(() => {
      clone.remove();
      cart.classList.remove('is-catch');
      void (/** @type {HTMLElement} */ (cart).offsetWidth);
      cart.classList.add('is-catch');
      cart.addEventListener('animationend', () => cart.classList.remove('is-catch'), { once: true });
    });
}

/* ---------- Keyboard scrolling bridge (>=990px, .page-wrapper scroller) ---------- */

const KEY_SKIP = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), dialog[open], [role="slider"], [role="listbox"], [role="tablist"], [role="menu"], [role="grid"], [role="spinbutton"]';
// Space activates these (a focused link does not: Space on a link scrolls the page, as it does natively)
const SPACE_SKIP = 'button, summary, [role="button"], [role="checkbox"], [role="radio"], [role="switch"], [role="option"], [role="tab"]';

/**
 * True when Chrome will not scroll .page-wrapper itself for this key target: <body>/<html>, anything outside
 * .page-wrapper (the toast), or anything inside a position:fixed layer within it (the nav bar).
 * @param {Element} target
 * @param {Element} wrapper
 */
function outsideScroller(target, wrapper) {
  if (target === document.body || target === document.documentElement) return true;
  if (!wrapper.contains(target)) return true;
  for (let el = /** @type {Element | null} */ (target); el && el !== wrapper; el = el.parentElement) {
    if (getComputedStyle(el).position === 'fixed') return true;
  }
  return false;
}

/** @param {KeyboardEvent} event */
function bridgeKeyScroll(event) {
  if (!scrollContainerMediaQuery.matches || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
  const key = event.key;
  if (!['PageDown', 'PageUp', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) return;
  // Keys from inside a shadow root (the Clarity Brand Agent chat) reach the document retargeted to the shadow host,
  // so test the composed path: closest() would miss the real input and Space would scroll instead of typing
  const path = event.composedPath().filter((node) => node instanceof Element);
  const target = path[0] ?? document.body;
  if (path.some((el) => el.matches(KEY_SKIP) || (key === ' ' && el.matches(SPACE_SKIP)))) return;
  if (document.documentElement.hasAttribute('scroll-lock') || document.querySelector('dialog[open]')) return;
  const wrapper = getScrollContainer();
  if (!wrapper || wrapper === document.scrollingElement || wrapper === document.documentElement) return;
  if (!outsideScroller(target, wrapper)) return;

  const page = Math.max(wrapper.clientHeight * 0.85, wrapper.clientHeight - 120);
  const behavior = reduceQuery.matches || event.repeat ? 'instant' : 'smooth';
  /** @type {number | null} */
  let by = null;
  if (key === 'PageDown' || (key === ' ' && !event.shiftKey)) by = page;
  else if (key === 'PageUp' || key === ' ') by = -page;
  else if (key === 'ArrowDown') by = 48;
  else if (key === 'ArrowUp') by = -48;

  if (by !== null) wrapper.scrollBy({ top: by, behavior: /** @type {ScrollBehavior} */ (behavior) });
  else wrapper.scrollTo({ top: key === 'Home' ? 0 : wrapper.scrollHeight, behavior: /** @type {ScrollBehavior} */ (behavior) });
  event.preventDefault();
}

/**
 * Keyboard focus must never land on content that is still reveal-hidden (WCAG 2.4.7). Focus scrolls a target only
 * to the nearest edge, which can sit inside the observer's bottom margin, so the observer alone may never reveal it.
 * Reveal every hidden ancestor of the focused element at once: no fade and no stagger delay.
 * @param {FocusEvent} event
 */
function revealOnFocus(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  /** @type {Element | null} */
  let el = target.closest('[data-ado-reveal]:not(.is-in)');
  while (el instanceof HTMLElement || el instanceof SVGElement) {
    const inline = el.style.transition;
    el.style.transition = 'none';
    el.classList.add('is-in');
    revealIO?.unobserve(el);
    void getComputedStyle(el).opacity;
    const node = el;
    requestAnimationFrame(() => {
      node.style.transition = inline;
    });
    el = el.parentElement?.closest('[data-ado-reveal]:not(.is-in)') ?? null;
  }
}

/**
 * Content that arrives later (the shop's sort, the cart's AJAX re-render, a section morph) is observed as it lands,
 * so its reveals play and its [data-ado-inview] animations (the touch tube float, clouds) are not left paused.
 * Removed content is unobserved. Records are settled at the end of each batch, so a node that is moved (removed
 * and re-added in the same task) stays observed.
 * @param {MutationRecord[]} records
 */
function onDomChange(records) {
  if (!revealIO || !inviewIO) return;
  /** @type {Set<Element>} */
  const added = new Set();
  /** @type {Set<Element>} */
  const removed = new Set();
  for (const record of records) {
    for (const node of record.addedNodes) if (node instanceof Element) added.add(node);
    for (const node of record.removedNodes) if (node instanceof Element) removed.add(node);
  }
  for (const el of removed) {
    if (el.isConnected) continue;
    for (const target of [el, ...el.querySelectorAll('[data-ado-reveal], [data-ado-inview]')]) {
      revealIO.unobserve(target);
      inviewIO.unobserve(target);
    }
  }
  for (const el of added) {
    if (!el.isConnected) continue;
    if (el.matches('[data-ado-inview]')) inviewIO.observe(el);
    if (el.matches('[data-ado-reveal]:not(.is-in)')) {
      if (reduceQuery.matches) el.classList.add('is-in');
      else revealIO.observe(el);
    }
    if (el.firstElementChild) scan(el);
  }
}

build();
new MutationObserver(onDomChange).observe(document.body, { childList: true, subtree: true });
document.addEventListener('focusin', revealOnFocus);
document.addEventListener('keydown', bridgeKeyScroll);
scrollContainerMediaQuery.addEventListener('change', build);
reduceQuery.addEventListener('change', () => scan(document));
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) scan(event.target);
});
